import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      toast.success("Account created");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />

      {/* Left - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial" />
        <div className="max-w-md text-center px-12 relative z-10">
          <img src="/assets/logo.png" alt="MasteringMate" className="h-20 opacity-10 mx-auto" />
          <h2 className="text-2xl font-['Rajdhani'] tracking-wide mt-4">
            START MASTERING YOUR DELIVERABLES
          </h2>
          <p className="text-cyan-200/40 leading-relaxed mt-4">
            Join agencies who trust MasteringMate for their video post production needs.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 relative">
        <div className="w-full max-w-sm">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-cyan-400/60 hover:text-cyan-300 transition-colors mb-16"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>

          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-['Rajdhani'] tracking-wide mb-2">CREATE ACCOUNT</h1>
              <p className="text-cyan-200/40">Get started with MasteringMate</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm text-cyan-300/60">Name</Label>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-12 rounded-sm"
                  data-testid="register-name"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-cyan-300/60">Email</Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 rounded-sm"
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-cyan-300/60">Password</Label>
                <Input
                  type="password"
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 rounded-sm"
                  data-testid="register-password"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-cyan-300/60">Confirm Password</Label>
                <Input
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-12 rounded-sm"
                  data-testid="register-confirm-password"
                />
              </div>

              {error && (
                <p className="text-sm text-red-400" data-testid="register-error">{error}</p>
              )}

              <Button 
                type="submit" 
                className="w-full h-12 rounded-sm btn-primary font-['Rajdhani'] tracking-wider"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>CREATE ACCOUNT <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </form>

            <p className="text-sm text-cyan-200/40 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-cyan-400 hover:text-cyan-300" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
